import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQueryWithReauth';
import { getAuthBaseUrl } from './api';

// ========== PORTAL PAGE PERMISSIONS (CRUD) ==========

// CRUD actions per page
export const CRUD_ACTIONS = [
  { key: 'view', label: 'View', color: 'info' },
  { key: 'create', label: 'Create', color: 'success' },
  { key: 'edit', label: 'Edit', color: 'warning' },
  { key: 'delete', label: 'Delete', color: 'error' },
];

// Admin portal pages
export const ADMIN_PORTAL_PAGES = [
  { key: 'dashboard', label: 'Dashboard', description: 'Admin dashboard', group: null },
  {
    key: 'user_management',
    label: 'User Management',
    description: 'Manage users and roles',
    group: 'Users',
  },
  {
    key: 'groups_roles',
    label: 'Groups & Roles',
    description: 'Group and role management',
    group: 'Users',
  },
  {
    key: 'sales_representatives',
    label: 'Sales Representatives',
    description: 'Sales rep management',
    group: 'Users',
  },
  {
    key: 'organization_management',
    label: 'Organization Management',
    description: 'Manage organizations',
    group: 'Organizations',
  },
  {
    key: 'vehicle_management',
    label: 'Vehicle Management',
    description: 'Manage vehicles',
    group: 'Fleet',
  },
  {
    key: 'device_management',
    label: 'Device Management',
    description: 'Manage devices',
    group: 'Fleet',
  },
  {
    key: 'technician_management',
    label: 'Technician Management',
    description: 'Manage technicians',
    group: 'Operations',
  },
  {
    key: 'branch_management',
    label: 'Branch Management',
    description: 'Manage branches',
    group: 'Operations',
  },
  {
    key: 'geofence_management',
    label: 'Geofence Management',
    description: 'Manage geofences',
    group: 'Operations',
  },
  {
    key: 'task_management',
    label: 'Task Management',
    description: 'Manage tasks',
    group: 'Operations',
  },
  {
    key: 'support_tickets',
    label: 'Support Tickets',
    description: 'View support tickets',
    group: 'Support',
  },
  {
    key: 'my_assigned_tickets',
    label: 'My Assigned Tickets',
    description: 'View assigned tickets',
    group: 'Support',
  },
  {
    key: 'support_agent_management',
    label: 'Support Agent Management',
    description: 'Manage support agents',
    group: 'Support',
  },
  {
    key: 'assignment_dashboard',
    label: 'Assignment Dashboard',
    description: 'Ticket assignments',
    group: 'Support',
  },
  {
    key: 'operations_center',
    label: 'Operations Center',
    description: 'Operations hub',
    group: 'System',
  },
  {
    key: 'testing_operations',
    label: 'Testing Operations',
    description: 'Testing tools',
    group: 'System',
  },
  {
    key: 'maintenance_mode',
    label: 'Maintenance Mode',
    description: 'System maintenance',
    group: 'System',
  },
  {
    key: 'reports_analytics',
    label: 'Reports & Analytics',
    description: 'Reports hub',
    group: 'Reports',
  },
  {
    key: 'user_reports',
    label: 'User Reports',
    description: 'User activity reports',
    group: 'Reports',
  },
  {
    key: 'fleet_reports',
    label: 'Fleet Reports',
    description: 'Fleet analytics',
    group: 'Reports',
  },
  {
    key: 'system_reports',
    label: 'System Reports',
    description: 'System health reports',
    group: 'Reports',
  },
  {
    key: 'suppression_management',
    label: 'Suppression Management',
    description: 'Alert suppression',
    group: 'System',
  },
  {
    key: 'sync_management',
    label: 'Sync Management',
    description: 'Data sync management',
    group: 'System',
  },
  {
    key: 'development_tools',
    label: 'Development Tools',
    description: 'Dev tools',
    group: 'System',
  },
  { key: 'settings', label: 'Settings', description: 'Admin settings', group: null },
];

// Bureau portal pages
export const BUREAU_PORTAL_PAGES = [
  { key: 'dashboard', label: 'Dashboard', description: 'Bureau dashboard', group: null },
  {
    key: 'alerts_management',
    label: 'Alerts Management',
    description: 'Alert monitoring',
    group: 'Alerts',
  },
  {
    key: 'alert_detail',
    label: 'Alert Detail',
    description: 'View alert details',
    group: 'Alerts',
  },
  { key: 'create_alert', label: 'Create Alert', description: 'Create new alerts', group: 'Alerts' },
  {
    key: 'events_management',
    label: 'Events Management',
    description: 'Event monitoring',
    group: 'Events',
  },
  {
    key: 'event_detail',
    label: 'Event Detail',
    description: 'View event details',
    group: 'Events',
  },
  { key: 'create_event', label: 'Create Event', description: 'Create new events', group: 'Events' },
  {
    key: 'suppression_management',
    label: 'Suppression Management',
    description: 'Suppression hub',
    group: 'Suppression',
  },
  {
    key: 'suppression_profiles',
    label: 'Suppression Profiles',
    description: 'Manage profiles',
    group: 'Suppression',
  },
  {
    key: 'device_overrides',
    label: 'Device Overrides',
    description: 'Device-level overrides',
    group: 'Suppression',
  },
  {
    key: 'suppression_analytics',
    label: 'Suppression Analytics',
    description: 'Suppression stats',
    group: 'Suppression',
  },
  {
    key: 'monitoring_tools',
    label: 'Monitoring Tools',
    description: 'Monitoring hub',
    group: 'Monitoring',
  },
  {
    key: 'realtime_monitoring',
    label: 'Real-time Monitoring',
    description: 'Live monitoring',
    group: 'Monitoring',
  },
  {
    key: 'system_diagnostics',
    label: 'System Diagnostics',
    description: 'System diagnostics',
    group: 'Monitoring',
  },
  {
    key: 'testing_interface',
    label: 'Testing Interface',
    description: 'Testing tools',
    group: 'Testing',
  },
  { key: 'alert_testing', label: 'Alert Testing', description: 'Test alerts', group: 'Testing' },
  { key: 'device_testing', label: 'Device Testing', description: 'Test devices', group: 'Testing' },
  { key: 'tasks', label: 'Tasks', description: 'Task management', group: null },
  { key: 'support_tickets', label: 'Support Tickets', description: 'Support tickets', group: null },
  { key: 'notifications', label: 'Notifications', description: 'Notifications', group: null },
  { key: 'settings', label: 'Settings', description: 'Bureau settings', group: null },
];

// Client portal pages (expanded with sub-pages)
export const CLIENT_PORTAL_PAGES = [
  { key: 'dashboard', label: 'Dashboard', description: 'Main dashboard', group: null },
  { key: 'vehicles', label: 'Vehicles', description: 'Vehicle management', group: 'Fleet' },
  { key: 'drivers', label: 'Drivers', description: 'Driver management', group: 'Fleet' },
  { key: 'devices', label: 'Devices', description: 'Device management', group: 'Fleet' },
  {
    key: 'fleet_overview',
    label: 'Fleet Overview',
    description: 'Fleet status overview',
    group: 'Fleet',
  },
  {
    key: 'fleet_analytics',
    label: 'Fleet Analytics',
    description: 'Fleet analytics',
    group: 'Fleet',
  },
  {
    key: 'fleet_efficiency',
    label: 'Fleet Efficiency',
    description: 'Efficiency metrics',
    group: 'Fleet',
  },
  {
    key: 'fleet_maintenance',
    label: 'Fleet Maintenance',
    description: 'Maintenance tracking',
    group: 'Fleet',
  },
  {
    key: 'events_tracking',
    label: 'Events Tracking',
    description: 'Event monitoring',
    group: 'Events',
  },
  { key: 'event_alerts', label: 'Event Alerts', description: 'Alert management', group: 'Events' },
  {
    key: 'event_timeline',
    label: 'Event Timeline',
    description: 'Event timeline',
    group: 'Events',
  },
  { key: 'reports_center', label: 'Reports Center', description: 'Report hub', group: 'Reports' },
  {
    key: 'fleet_summary_report',
    label: 'Fleet Summary',
    description: 'Fleet summary report',
    group: 'Reports',
  },
  {
    key: 'driver_behavior_report',
    label: 'Driver Behavior',
    description: 'Driver behavior analysis',
    group: 'Reports',
  },
  {
    key: 'fuel_efficiency_report',
    label: 'Fuel Efficiency',
    description: 'Fuel efficiency report',
    group: 'Reports',
  },
  {
    key: 'maintenance_report',
    label: 'Maintenance Report',
    description: 'Maintenance analytics',
    group: 'Reports',
  },
  {
    key: 'custom_reports',
    label: 'Custom Reports',
    description: 'Custom reports',
    group: 'Reports',
  },
  { key: 'fleet_chat', label: 'FleetChat', description: 'Communication', group: null },
  { key: 'support_tickets', label: 'Support Tickets', description: 'Support tickets', group: null },
  { key: 'notifications', label: 'Notifications', description: 'Notifications', group: null },
  {
    key: 'health_monitoring',
    label: 'Health Monitoring',
    description: 'Health overview',
    group: 'Health',
  },
  {
    key: 'fleet_health_overview',
    label: 'Fleet Health Overview',
    description: 'Fleet health',
    group: 'Health',
  },
  {
    key: 'vehicle_diagnostics',
    label: 'Vehicle Diagnostics',
    description: 'Vehicle diagnostics',
    group: 'Health',
  },
  {
    key: 'maintenance_alerts',
    label: 'Maintenance Alerts',
    description: 'Maintenance alerts',
    group: 'Health',
  },
  { key: 'settings', label: 'Settings', description: 'General settings', group: 'Settings' },
  {
    key: 'profile_settings',
    label: 'Profile Settings',
    description: 'Profile settings',
    group: 'Settings',
  },
  {
    key: 'notification_settings',
    label: 'Notification Settings',
    description: 'Notification prefs',
    group: 'Settings',
  },
  {
    key: 'user_preferences',
    label: 'User Preferences',
    description: 'User preferences',
    group: 'Settings',
  },
  { key: 'help_support', label: 'Help & Support', description: 'Help documentation', group: null },
];

// Sales portal pages
// `actions` limits which CRUD checkboxes are applicable. Omit for full CRUD.
export const SALES_PORTAL_PAGES = [
  { key: 'dashboard', label: 'Dashboard', description: 'Sales dashboard', group: null, actions: ['view'] },
  // Clients
  { key: 'clients', label: 'Clients Page', description: 'Access to client management page', group: 'Clients', actions: ['view'] },
  { key: 'clients_own', label: 'Own Clients', description: 'View only own assigned clients', group: 'Clients', actions: ['view'] },
  { key: 'clients_all', label: 'All Clients', description: 'View all clients across the team', group: 'Clients', actions: ['view'] },
  // Bureau Intake
  { key: 'bureau_intake', label: 'Bureau Intake Page', description: 'Access to intake page', group: 'Bureau Intake', actions: ['view'] },
  { key: 'bureau_intake_own', label: 'Own Intake Forms', description: 'CRUD own intake forms', group: 'Bureau Intake' },
  { key: 'bureau_intake_all', label: 'All Intake Forms', description: 'CRUD all intake forms (any user)', group: 'Bureau Intake', actions: ['view', 'edit', 'delete'] },
  { key: 'bureau_intake_send', label: 'Send to Client', description: 'Send intake forms to clients via email', group: 'Bureau Intake', actions: ['view', 'create'] },
  { key: 'bureau_intake_analytics', label: 'Intake Analytics', description: 'Personal & system intake analytics', group: 'Bureau Intake', actions: ['view'] },
  { key: 'bureau_intake_export', label: 'Export Zones', description: 'Export zones/data (GPSWox .gexp)', group: 'Bureau Intake', actions: ['view'] },
  // Action Requests
  { key: 'action_requests', label: 'Action Requests Page', description: 'Access to action requests page', group: 'Action Requests', actions: ['view'] },
  { key: 'action_requests_own', label: 'Own Action Requests', description: 'Manage own action requests', group: 'Action Requests' },
  { key: 'action_requests_all', label: 'All Action Requests', description: 'Manage all action requests', group: 'Action Requests', actions: ['view', 'edit', 'delete'] },
  // Sales Staff
  { key: 'sales_staff', label: 'Sales Staff', description: 'Team management', group: 'Sales Staff', actions: ['view'] },
  { key: 'sales_staff_performance', label: 'Staff Performance', description: 'View performance metrics', group: 'Sales Staff', actions: ['view'] },
  // Settings
  { key: 'settings', label: 'Settings', description: 'Portal settings', group: null, actions: ['view', 'edit'] },
];

// Portal pages mapping
export const PORTAL_PAGES = {
  admin: ADMIN_PORTAL_PAGES,
  bureau: BUREAU_PORTAL_PAGES,
  client: CLIENT_PORTAL_PAGES,
  sales: SALES_PORTAL_PAGES,
};

// Portal display labels
export const PORTAL_OPTIONS = [
  { value: 'admin', label: 'Admin Portal' },
  { value: 'bureau', label: 'Bureau Portal' },
  { value: 'client', label: 'Client Portal' },
  { value: 'sales', label: 'Sales Portal' },
];

// Legacy ACCESS_LEVELS kept for backwards compat
export const ACCESS_LEVELS = [
  { value: 'none', label: 'No Access', color: 'error' },
  { value: 'view', label: 'View Only', color: 'warning' },
  { value: 'full', label: 'Full Access', color: 'success' },
];

// Generate default page permissions for a portal (CRUD format, view-only)
export const getDefaultPagePermissions = (portal = 'client') => {
  const pages = PORTAL_PAGES[portal] || CLIENT_PORTAL_PAGES;
  return pages.reduce((acc, page) => {
    const allowed = page.actions || ['view', 'create', 'edit', 'delete'];
    acc[page.key] = {
      view: allowed.includes('view'),
      create: false,
      edit: false,
      delete: false,
    };
    return acc;
  }, {});
};

// Generate full access permissions for a portal (CRUD format, respects applicable actions)
export const getFullAccessPermissions = (portal = 'client') => {
  const pages = PORTAL_PAGES[portal] || CLIENT_PORTAL_PAGES;
  return pages.reduce((acc, page) => {
    const allowed = page.actions || ['view', 'create', 'edit', 'delete'];
    acc[page.key] = {
      view: allowed.includes('view'),
      create: allowed.includes('create'),
      edit: allowed.includes('edit'),
      delete: allowed.includes('delete'),
    };
    return acc;
  }, {});
};

// Generate no access permissions for a portal (CRUD format)
export const getNoAccessPermissions = (portal = 'client') => {
  const pages = PORTAL_PAGES[portal] || CLIENT_PORTAL_PAGES;
  return pages.reduce((acc, page) => {
    acc[page.key] = { view: false, create: false, edit: false, delete: false };
    return acc;
  }, {});
};

/**
 * RBAC API Slice
 *
 * Provides RTK Query endpoints for RBAC functionality:
 * - User management
 * - Role management
 * - Organization management
 * - Permission checking
 * - Audit logs
 *
 * Now with automatic token refresh!
 */
export const rbacApiSlice = createApi({
  reducerPath: 'rbacApi',
  baseQuery: baseQueryWithReauth(getAuthBaseUrl()),
  tagTypes: [
    'User',
    'Role',
    'Organization',
    'UserRole',
    'AuditLog',
    'Permission',
    'Department',
    'Region',
  ],
  endpoints: (builder) => ({
    // Authentication endpoints
    login: builder.mutation({
      query: (credentials) => ({
        url: 'auth/login/',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['User', 'Permission'],
    }),

    refreshToken: builder.mutation({
      query: (refreshToken) => ({
        url: 'auth/refresh/',
        method: 'POST',
        body: { refresh: refreshToken },
      }),
    }),

    switchOrganization: builder.mutation({
      query: (organizationId) => ({
        url: 'switch-organization/',
        method: 'POST',
        body: { organization_id: organizationId },
      }),
      invalidatesTags: ['Permission', 'UserRole'],
    }),

    getUserPermissions: builder.query({
      query: () => 'auth/user-info/',
      providesTags: ['Permission'],
    }),

    // User management endpoints
    getUsers: builder.query({
      query: ({ organizationId, page = 1, pageSize = 25, search = '' } = {}) => ({
        url: 'users/',
        params: {
          organization: organizationId,
          page,
          page_size: pageSize,
          search,
        },
      }),
      providesTags: ['User'],
    }),

    getUser: builder.query({
      query: (userId) => `users/${userId}/`,
      providesTags: (result, error, userId) => [{ type: 'User', id: userId }],
    }),

    createUser: builder.mutation({
      query: (userData) => ({
        url: 'users/',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['User'],
    }),

    updateUser: builder.mutation({
      query: ({ userId, ...userData }) => ({
        url: `users/${userId}/`,
        method: 'PATCH',
        body: userData,
      }),
      invalidatesTags: (result, error, { userId }) => [{ type: 'User', id: userId }, 'User'],
    }),

    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `users/${userId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),

    activateUser: builder.mutation({
      query: (userId) => ({
        url: `users/${userId}/activate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, userId) => [{ type: 'User', id: userId }, 'User'],
    }),

    deactivateUser: builder.mutation({
      query: (userId) => ({
        url: `users/${userId}/deactivate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, userId) => [{ type: 'User', id: userId }, 'User'],
    }),

    // Role management endpoints (RBAC)
    getRoles: builder.query({
      query: ({
        organizationId,
        portal = '',
        level = '',
        isActive = '',
        canManageUsers = '',
        search = '',
        page_size,
      } = {}) => {
        const params = {
          search,
        };
        if (organizationId) params.organization = organizationId;
        if (portal) params.portal = portal;
        if (level) params.level = level;
        if (isActive !== '') params.is_active = isActive;
        if (canManageUsers !== '') params.can_manage_users = canManageUsers;
        if (page_size) params.page_size = page_size;

        return {
          url: 'roles/',
          params,
        };
      },
      providesTags: ['Role'],
    }),

    getRole: builder.query({
      query: (roleId) => `roles/${roleId}/`,
      providesTags: (result, error, roleId) => [{ type: 'Role', id: roleId }],
    }),

    createRole: builder.mutation({
      query: ({ organizationIds, ...roleData }) => ({
        url: 'roles/',
        method: 'POST',
        body: {
          ...roleData,
          organization_ids: organizationIds || [],
        },
      }),
      invalidatesTags: ['Role'],
    }),

    updateRole: builder.mutation({
      query: ({ roleId, roleUuid, organizationIds, ...roleData }) => ({
        url: `roles/${roleId}/`,
        method: 'PATCH',
        body: {
          ...roleData,
          ...(organizationIds !== undefined && { organization_ids: organizationIds }),
        },
      }),
      invalidatesTags: (result, error, { roleId, roleUuid }) => [
        { type: 'Role', id: roleUuid || roleId },
        'Role',
      ],
    }),

    deleteRole: builder.mutation({
      query: (roleUuid) => ({
        url: `roles/${roleUuid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Role'],
    }),

    activateRole: builder.mutation({
      query: (roleId) => ({
        url: `roles/${roleId}/activate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, roleId) => [{ type: 'Role', id: roleId }, 'Role'],
    }),

    deactivateRole: builder.mutation({
      query: (roleId) => ({
        url: `roles/${roleId}/deactivate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, roleId) => [{ type: 'Role', id: roleId }, 'Role'],
    }),

    // Role permission management
    assignRolePermissions: builder.mutation({
      query: ({ roleId, permissionIds }) => ({
        url: `roles/${roleId}/assign_permissions/`,
        method: 'POST',
        body: { permission_ids: permissionIds },
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'Role', id: roleId },
        'Role',
        'Permission',
      ],
    }),

    getRoleUsers: builder.query({
      query: (roleId) => `roles/${roleId}/users/`,
      providesTags: (result, error, roleId) => [{ type: 'Role', id: roleId }, 'UserRole'],
    }),

    // User-Role assignment endpoints (RBAC)
    getUserRoles: builder.query({
      query: ({
        userId,
        organizationId,
        departmentId,
        roleId,
        isActive,
        page = 1,
        pageSize = 25,
      } = {}) => {
        const params = {
          page,
          page_size: pageSize,
        };
        if (userId) params.user = userId;
        if (organizationId) params.organization = organizationId;
        if (departmentId) params.department = departmentId;
        if (roleId) params.role = roleId;
        if (isActive !== undefined && isActive !== '') params.is_active = isActive;

        return {
          url: 'user-roles/',
          params,
        };
      },
      providesTags: ['UserRole'],
    }),

    getUserRole: builder.query({
      query: (userRoleId) => `user-roles/${userRoleId}/`,
      providesTags: (result, error, userRoleId) => [{ type: 'UserRole', id: userRoleId }],
    }),

    assignRole: builder.mutation({
      query: (assignmentData) => ({
        url: 'user-roles/',
        method: 'POST',
        body: assignmentData,
      }),
      invalidatesTags: ['UserRole', 'Permission'],
    }),

    updateUserRole: builder.mutation({
      query: ({ userRoleId, ...data }) => ({
        url: `user-roles/${userRoleId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { userRoleId }) => [
        { type: 'UserRole', id: userRoleId },
        'UserRole',
        'Permission',
      ],
    }),

    removeRole: builder.mutation({
      query: (assignmentId) => ({
        url: `user-roles/${assignmentId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserRole', 'Permission'],
    }),

    // Organization management endpoints (RBAC)
    getOrganizations: builder.query({
      query: ({
        page = 1,
        page_size = 25,
        search = '',
        plan = '',
        is_active = '',
        parent = '',
      } = {}) => {
        // Build params object, filtering out empty values
        const params = {
          page,
          page_size,
        };

        if (search) params.search = search;
        if (plan) params.plan = plan;
        if (is_active !== '') params.is_active = is_active;
        if (parent) params.parent = parent;

        return {
          url: 'organizations/',
          params,
        };
      },
      // Fine-grained cache tags for selective invalidation
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map((org) => ({ type: 'Organization', id: org.uuid || org.id })),
              { type: 'Organization', id: 'LIST' },
            ]
          : [{ type: 'Organization', id: 'LIST' }],
    }),

    getOrganization: builder.query({
      query: (orgId) => `organizations/${orgId}/`,
      providesTags: (result, error, orgId) => [{ type: 'Organization', id: orgId }],
    }),

    createOrganization: builder.mutation({
      query: (orgData) => ({
        url: 'organizations/',
        method: 'POST',
        body: orgData,
      }),
      // Optimistic update - show new org immediately
      async onQueryStarted(orgData, { dispatch, queryFulfilled }) {
        // Add optimistic entry to cache (for first page)
        const patchResult = dispatch(
          rbacApiSlice.util.updateQueryData(
            'getOrganizations',
            { page: 1, page_size: 25 },
            (draft) => {
              if (draft?.results) {
                draft.results.unshift({
                  uuid: `temp-${Date.now()}`,
                  name: orgData.name,
                  code: orgData.code,
                  description: orgData.description || '',
                  is_active: true,
                  status: orgData.status || 'active',
                  plan: orgData.plan || ['ad_hoc'],
                  _optimistic: true,
                });
                draft.count = (draft.count || 0) + 1;
              }
            }
          )
        );
        try {
          const { data } = await queryFulfilled;
          // Replace optimistic entry with real data
          dispatch(
            rbacApiSlice.util.updateQueryData(
              'getOrganizations',
              { page: 1, page_size: 25 },
              (draft) => {
                if (draft?.results) {
                  const idx = draft.results.findIndex((o) => o._optimistic);
                  if (idx !== -1 && data) {
                    draft.results[idx] = data;
                  }
                }
              }
            )
          );
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [{ type: 'Organization', id: 'LIST' }],
    }),

    updateOrganization: builder.mutation({
      query: ({ orgUuid, ...orgData }) => ({
        url: `organizations/${orgUuid}/`,
        method: 'PATCH',
        body: orgData,
      }),
      // Optimistic update for edits
      async onQueryStarted({ orgUuid, ...orgData }, { dispatch, queryFulfilled }) {
        // Update the organization in cache immediately
        const patchResult = dispatch(
          rbacApiSlice.util.updateQueryData(
            'getOrganizations',
            { page: 1, page_size: 25 },
            (draft) => {
              if (draft?.results) {
                const org = draft.results.find((o) => o.uuid === orgUuid);
                if (org) {
                  Object.assign(org, orgData);
                }
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { orgUuid }) => [
        { type: 'Organization', id: orgUuid },
        { type: 'Organization', id: 'LIST' },
      ],
    }),

    deleteOrganization: builder.mutation({
      query: (orgUuid) => ({
        url: `organizations/${orgUuid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Organization'],
    }),

    // Organization hierarchy and users
    getOrganizationHierarchy: builder.query({
      query: (orgId) => `organizations/${orgId}/hierarchy/`,
      providesTags: (result, error, orgId) => [{ type: 'Organization', id: orgId }, 'Department'],
    }),

    getOrganizationUsers: builder.query({
      query: (orgId) => `organizations/${orgId}/users/`,
      providesTags: (result, error, orgId) => [{ type: 'Organization', id: orgId }, 'UserRole'],
    }),

    deactivateOrganization: builder.mutation({
      query: (orgId) => ({
        url: `organizations/${orgId}/deactivate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, orgId) => [
        { type: 'Organization', id: orgId },
        'Organization',
        'UserRole',
      ],
    }),

    activateOrganization: builder.mutation({
      query: (orgId) => ({
        url: `organizations/${orgId}/activate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, orgId) => [
        { type: 'Organization', id: orgId },
        'Organization',
        'UserRole',
      ],
    }),

    getOrganizationStats: builder.query({
      query: () => 'organizations/stats/',
      providesTags: ['Organization'],
    }),

    getOrganizationDeletionImpact: builder.query({
      query: (orgUuid) => `organizations/${orgUuid}/deletion_impact/`,
      providesTags: (result, error, orgUuid) => [{ type: 'Organization', id: orgUuid }],
    }),

    // Department management endpoints (RBAC)
    getDepartments: builder.query({
      query: ({ organizationId, parentId, isActive, search, page = 1, pageSize = 25 } = {}) => {
        const params = {
          page,
          page_size: pageSize,
        };
        if (organizationId) params.organization = organizationId;
        if (parentId) params.parent_department = parentId;
        if (isActive !== undefined && isActive !== '') params.is_active = isActive;
        if (search) params.search = search;

        return {
          url: 'departments/',
          params,
        };
      },
      providesTags: ['Department'],
    }),

    getDepartment: builder.query({
      query: (deptId) => `departments/${deptId}/`,
      providesTags: (result, error, deptId) => [{ type: 'Department', id: deptId }],
    }),

    createDepartment: builder.mutation({
      query: (deptData) => ({
        url: 'departments/',
        method: 'POST',
        body: deptData,
      }),
      invalidatesTags: ['Department', 'Organization'],
    }),

    updateDepartment: builder.mutation({
      query: ({ deptUuid, ...deptData }) => ({
        url: `departments/${deptUuid}/`,
        method: 'PATCH',
        body: deptData,
      }),
      invalidatesTags: (result, error, { deptUuid }) => [
        { type: 'Department', id: deptUuid },
        'Department',
      ],
    }),

    deleteDepartment: builder.mutation({
      query: (deptUuid) => ({
        url: `departments/${deptUuid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Department'],
    }),

    activateDepartment: builder.mutation({
      query: (deptId) => ({
        url: `departments/${deptId}/activate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, deptId) => [
        { type: 'Department', id: deptId },
        'Department',
      ],
    }),

    deactivateDepartment: builder.mutation({
      query: (deptId) => ({
        url: `departments/${deptId}/deactivate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, deptId) => [
        { type: 'Department', id: deptId },
        'Department',
      ],
    }),

    // Region management endpoints
    getRegions: builder.query({
      query: ({
        organizationId,
        parentId,
        type,
        isActive,
        search,
        page = 1,
        pageSize = 25,
      } = {}) => {
        const params = {
          page,
          page_size: pageSize,
        };
        if (organizationId) params.organization = organizationId;
        if (parentId) params.parent = parentId;
        if (type) params.type = type;
        if (isActive !== undefined && isActive !== '') params.is_active = isActive;
        if (search) params.search = search;

        return {
          url: 'regions/',
          params,
        };
      },
      providesTags: ['Region'],
    }),

    getRegion: builder.query({
      query: (regionId) => `regions/${regionId}/`,
      providesTags: (result, error, regionId) => [{ type: 'Region', id: regionId }],
    }),

    createRegion: builder.mutation({
      query: (regionData) => ({
        url: 'regions/',
        method: 'POST',
        body: regionData,
      }),
      invalidatesTags: ['Region', 'Organization'],
    }),

    updateRegion: builder.mutation({
      query: ({ regionUuid, ...regionData }) => ({
        url: `regions/${regionUuid}/`,
        method: 'PATCH',
        body: regionData,
      }),
      invalidatesTags: (result, error, { regionUuid }) => [
        { type: 'Region', id: regionUuid },
        'Region',
      ],
    }),

    deleteRegion: builder.mutation({
      query: (regionUuid) => ({
        url: `regions/${regionUuid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Region'],
    }),

    activateRegion: builder.mutation({
      query: (regionId) => ({
        url: `regions/${regionId}/activate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, regionId) => [{ type: 'Region', id: regionId }, 'Region'],
    }),

    deactivateRegion: builder.mutation({
      query: (regionId) => ({
        url: `regions/${regionId}/deactivate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, regionId) => [{ type: 'Region', id: regionId }, 'Region'],
    }),

    // Audit log endpoints
    getAuditLogs: builder.query({
      query: ({
        page = 1,
        pageSize = 25,
        eventType = '',
        severity = '',
        userId = '',
        organizationId = '',
        startDate = '',
        endDate = '',
        search = '',
      } = {}) => ({
        url: 'audit-logs/',
        params: {
          page,
          page_size: pageSize,
          event_type: eventType,
          severity,
          user: userId,
          organization: organizationId,
          start_date: startDate,
          end_date: endDate,
          search,
        },
      }),
      providesTags: ['AuditLog'],
    }),

    getAuditLogStats: builder.query({
      query: ({ startDate = '', endDate = '', organizationId = '' } = {}) => ({
        url: 'audit-logs/stats/',
        params: {
          start_date: startDate,
          end_date: endDate,
          organization: organizationId,
        },
      }),
      providesTags: ['AuditLog'],
    }),

    exportAuditLogs: builder.mutation({
      query: (filters) => ({
        url: 'audit-logs/export/',
        method: 'POST',
        body: filters,
        responseHandler: 'blob',
      }),
    }),

    // Permission management endpoints (RBAC)
    getPermissions: builder.query({
      query: ({ search = '', contentType = '', codename = '' } = {}) => ({
        url: 'permissions/',
        params: {
          search,
          content_type: contentType,
          codename,
        },
      }),
      providesTags: ['Permission'],
    }),

    getAvailablePermissions: builder.query({
      query: ({ search = '', contentType = '', codename = '' } = {}) => ({
        url: 'permissions/',
        params: {
          search,
          content_type: contentType,
          codename,
        },
      }),
      providesTags: ['Permission'],
    }),

    getPermissionsByCategory: builder.query({
      query: () => 'permissions/by_category/',
      providesTags: ['Permission'],
    }),

    createPermission: builder.mutation({
      query: (permissionData) => ({
        url: 'permissions/',
        method: 'POST',
        body: permissionData,
      }),
      invalidatesTags: ['Permission'],
    }),

    updatePermission: builder.mutation({
      query: ({ permissionId, permissionUuid, ...permissionData }) => ({
        url: `permissions/${permissionUuid || permissionId}/`,
        method: 'PATCH',
        body: permissionData,
      }),
      invalidatesTags: (result, error, { permissionId, permissionUuid }) => [
        { type: 'Permission', id: permissionUuid || permissionId },
        'Permission',
      ],
    }),

    deletePermission: builder.mutation({
      query: (permissionUuid) => ({
        url: `permissions/${permissionUuid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Permission'],
    }),

    assignPermissionsToRole: builder.mutation({
      query: ({ roleId, permissionIds }) => ({
        url: `roles/${roleId}/assign_permissions/`,
        method: 'POST',
        body: { permission_ids: permissionIds },
      }),
      invalidatesTags: ['Role', 'Permission'],
    }),

    // Dashboard and statistics endpoints
    getDashboardStats: builder.query({
      query: ({ organizationId = '' } = {}) => ({
        url: 'dashboard/stats/',
        params: {
          organization: organizationId,
        },
      }),
      providesTags: ['User', 'Role', 'Organization'],
    }),

    getSystemHealth: builder.query({
      query: () => 'system/health/',
      providesTags: ['Organization'],
    }),

    // Bulk operations
    bulkUpdateUsers: builder.mutation({
      query: ({ action, userIds, data = {} }) => ({
        url: 'users/bulk/',
        method: 'POST',
        body: {
          action,
          user_ids: userIds,
          data,
        },
      }),
      invalidatesTags: ['User'],
    }),

    bulkAssignRoles: builder.mutation({
      query: ({ userIds, roleId, organizationId, departmentId = null }) => ({
        url: 'user-roles/bulk_assign/',
        method: 'POST',
        body: {
          user_ids: userIds,
          role_id: roleId,
          organization_id: organizationId,
          department_id: departmentId,
        },
      }),
      invalidatesTags: ['UserRole', 'Permission'],
    }),

    // Security endpoints
    lockUser: builder.mutation({
      query: ({ userId, duration = 30 }) => ({
        url: `users/${userId}/lock/`,
        method: 'POST',
        body: { duration_minutes: duration },
      }),
      invalidatesTags: (result, error, { userId }) => [{ type: 'User', id: userId }],
    }),

    unlockUser: builder.mutation({
      query: (userId) => ({
        url: `users/${userId}/unlock/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, userId) => [{ type: 'User', id: userId }],
    }),

    resetUserPassword: builder.mutation({
      query: ({ userId, newPassword = null, sendEmail = true }) => ({
        url: `users/${userId}/reset-password/`,
        method: 'POST',
        body: {
          new_password: newPassword,
          send_email: sendEmail,
        },
      }),
      invalidatesTags: (result, error, { userId }) => [{ type: 'User', id: userId }],
    }),

    // Session management
    getSessions: builder.query({
      query: (userId) => `users/${userId}/sessions/`,
      providesTags: (result, error, userId) => [{ type: 'User', id: userId }],
    }),

    terminateSession: builder.mutation({
      query: ({ userId, sessionId }) => ({
        url: `users/${userId}/sessions/${sessionId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { userId }) => [{ type: 'User', id: userId }],
    }),

    // Client Groups (for filtering vehicles/devices)
    getClientGroups: builder.query({
      query: () => ({
        url:
          process.env.NODE_ENV === 'production'
            ? '/api/fleet/device-groups/'
            : 'http://localhost:8000/api/fleet/device-groups/',
        method: 'GET',
      }),
      providesTags: ['ClientGroup'],
    }),

    // ===== CLIENT RBAC SETUP ENDPOINTS =====
    // For client onboarding wizard RBAC integration

    // Get role templates for client setup
    getRoleTemplates: builder.query({
      query: () => 'client-rbac/role-templates/',
      providesTags: ['RoleTemplate'],
    }),

    // Create organization from client data
    createOrganizationFromClient: builder.mutation({
      query: (clientData) => ({
        url: 'client-rbac/create-organization/',
        method: 'POST',
        body: clientData,
      }),
      invalidatesTags: ['Organization', 'Department', 'Role'],
    }),

    // Assign client to organization
    assignClientToOrganization: builder.mutation({
      query: ({ clientId, organizationId }) => ({
        url: 'client-rbac/assign-organization/',
        method: 'POST',
        body: { client_id: clientId, organization_id: organizationId },
      }),
      invalidatesTags: ['Organization'],
    }),

    // Create default departments for organization
    createDefaultDepartments: builder.mutation({
      query: ({ organizationId, template = 'fleet_basic' }) => ({
        url: 'client-rbac/create-departments/',
        method: 'POST',
        body: { organization_id: organizationId, template },
      }),
      invalidatesTags: ['Department', 'Organization'],
    }),

    // Apply role template to organization
    applyRoleTemplate: builder.mutation({
      query: ({ organizationId, template, customRoles = [] }) => ({
        url: 'client-rbac/apply-role-template/',
        method: 'POST',
        body: {
          organization_id: organizationId,
          template,
          custom_roles: customRoles,
        },
      }),
      invalidatesTags: ['Role', 'Organization'],
    }),

    // Create initial admin user for client
    createInitialAdminUser: builder.mutation({
      query: ({ organizationId, contactData }) => ({
        url: 'client-rbac/create-admin-user/',
        method: 'POST',
        body: { organization_id: organizationId, contact: contactData },
      }),
      invalidatesTags: ['User', 'UserRole'],
    }),

    // Assign user roles in bulk during client setup
    assignUserRoles: builder.mutation({
      query: ({ organizationId, userRoleAssignments }) => ({
        url: 'client-rbac/assign-user-roles/',
        method: 'POST',
        body: {
          organization_id: organizationId,
          assignments: userRoleAssignments,
        },
      }),
      invalidatesTags: ['UserRole', 'User'],
    }),

    // Get client RBAC summary
    getClientRbacSummary: builder.query({
      query: (clientId) => `client-rbac/summary/${clientId}/`,
      providesTags: (result, error, clientId) => [
        { type: 'Organization', id: 'client' },
        { type: 'Client', id: clientId },
      ],
    }),

    // Validate client RBAC data before submission
    validateClientRbacData: builder.mutation({
      query: (clientData) => ({
        url: 'client-rbac/validate/',
        method: 'POST',
        body: clientData,
      }),
    }),

    // Super Admin User Management (no onboarding flow)
    getSuperAdminUsers: builder.query({
      query: (params) => ({
        url: 'super-admin/users/',
        params,
      }),
      providesTags: ['SuperAdminUser'],
    }),

    createSuperAdminUser: builder.mutation({
      query: (user) => ({
        url: 'super-admin/users/',
        method: 'POST',
        body: user,
      }),
      invalidatesTags: ['SuperAdminUser', 'User'],
    }),

    updateSuperAdminUser: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `super-admin/users/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['SuperAdminUser', 'User'],
    }),

    deleteSuperAdminUser: builder.mutation({
      query: (id) => ({
        url: `super-admin/users/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SuperAdminUser', 'User'],
    }),

    toggleSuperAdminUserStaff: builder.mutation({
      query: (id) => ({
        url: `super-admin/users/${id}/toggle_staff/`,
        method: 'POST',
      }),
      invalidatesTags: ['SuperAdminUser', 'User'],
    }),

    toggleSuperAdminUserSuperuser: builder.mutation({
      query: (id) => ({
        url: `super-admin/users/${id}/toggle_superuser/`,
        method: 'POST',
      }),
      invalidatesTags: ['SuperAdminUser', 'User'],
    }),

    suspendSuperAdminUser: builder.mutation({
      query: ({ id, reason }) => ({
        url: `super-admin/users/${id}/suspend/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['SuperAdminUser', 'User'],
    }),

    unsuspendSuperAdminUser: builder.mutation({
      query: (id) => ({
        url: `super-admin/users/${id}/unsuspend/`,
        method: 'POST',
      }),
      invalidatesTags: ['SuperAdminUser', 'User'],
    }),

    // RBAC Duplicate Analysis - Super Admin only
    getDuplicateAnalysis: builder.query({
      query: (params = {}) => ({
        url: 'duplicates/analysis/',
        params: {
          organization_limit: params.organizationLimit,
          include_details: params.includeDetails || false,
        },
      }),
      providesTags: ['Organization', 'Department', 'Role'],
      keepUnusedDataFor: 60, // Keep for 1 minute since this is expensive
    }),

    // RBAC Cleanup - Super Admin only
    runRbacCleanup: builder.mutation({
      query: (options) => ({
        url: 'duplicates/cleanup/',
        method: 'POST',
        body: {
          dry_run: options.dryRun || true,
          init_templates: options.initTemplates || false,
          cleanup_duplicates: options.cleanupDuplicates || false,
          migrate_to_templates: options.migrateToTemplates || false,
          organization_limit: options.organizationLimit || null,
        },
      }),
      invalidatesTags: ['Organization', 'Department', 'Role'],
    }),
  }),
});

// Export hooks for components to use
export const {
  // Authentication
  useLoginMutation,
  useRefreshTokenMutation,
  useSwitchOrganizationMutation,
  useGetUserPermissionsQuery,

  // Users
  useGetUsersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useActivateUserMutation,
  useDeactivateUserMutation,
  useBulkUpdateUsersMutation,
  useLockUserMutation,
  useUnlockUserMutation,
  useResetUserPasswordMutation,

  // Roles
  useGetRolesQuery,
  useGetRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useActivateRoleMutation,
  useDeactivateRoleMutation,
  useAssignRolePermissionsMutation,
  useGetRoleUsersQuery,

  // User Roles
  useGetUserRolesQuery,
  useGetUserRoleQuery,
  useAssignRoleMutation,
  useUpdateUserRoleMutation,
  useRemoveRoleMutation,
  useBulkAssignRolesMutation,

  // Organizations
  useGetOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useActivateOrganizationMutation,
  useDeactivateOrganizationMutation,
  useGetOrganizationHierarchyQuery,
  useGetOrganizationUsersQuery,
  useGetOrganizationStatsQuery,
  useGetOrganizationDeletionImpactQuery,

  // Departments
  useGetDepartmentsQuery,
  useGetDepartmentQuery,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
  useActivateDepartmentMutation,
  useDeactivateDepartmentMutation,

  // Regions
  useGetRegionsQuery,
  useGetRegionQuery,
  useCreateRegionMutation,
  useUpdateRegionMutation,
  useDeleteRegionMutation,
  useActivateRegionMutation,
  useDeactivateRegionMutation,

  // Audit Logs
  useGetAuditLogsQuery,
  useGetAuditLogStatsQuery,
  useExportAuditLogsMutation,

  // Permissions
  useGetPermissionsQuery,
  useGetAvailablePermissionsQuery,
  useGetPermissionsByCategoryQuery,
  useCreatePermissionMutation,
  useUpdatePermissionMutation,
  useDeletePermissionMutation,
  useAssignPermissionsToRoleMutation,

  // Dashboard
  useGetDashboardStatsQuery,
  useGetSystemHealthQuery,

  // Sessions
  useGetSessionsQuery,
  useTerminateSessionMutation,

  // Client Groups
  useGetClientGroupsQuery,

  // Client RBAC Setup
  useGetRoleTemplatesQuery,
  useCreateOrganizationFromClientMutation,
  useAssignClientToOrganizationMutation,
  useCreateDefaultDepartmentsMutation,
  useApplyRoleTemplateMutation,
  useCreateInitialAdminUserMutation,
  useAssignUserRolesMutation,
  useGetClientRbacSummaryQuery,
  useValidateClientRbacDataMutation,

  // RBAC Duplicate Detection & Cleanup (Super Admin)
  useGetDuplicateAnalysisQuery,
  useRunRbacCleanupMutation,

  // Super Admin User Management
  useGetSuperAdminUsersQuery,
  useCreateSuperAdminUserMutation,
  useUpdateSuperAdminUserMutation,
  useDeleteSuperAdminUserMutation,
  useToggleSuperAdminUserStaffMutation,
  useToggleSuperAdminUserSuperuserMutation,
  useSuspendSuperAdminUserMutation,
  useUnsuspendSuperAdminUserMutation,
} = rbacApiSlice;
